/**
 * DashboardHero.tsx — the dashboard's focal tile (v2.1 visual redesign,
 * 2026-08-05: "no life, no visuals, two rows of numbers").
 *
 * A dark-sage gradient hero holding the ONLY big numbers on the page: up to
 * three action tiles (your approvals / intake / reviews / data entry —
 * whichever exist for your role), each one click from the work. When
 * nothing waits, the hero celebrates instead.
 */
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CelebrationOutlined,
  FactCheckOutlined,
  Inbox,
  RateReviewOutlined,
  ScienceOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import type { DashboardData } from '../../types/dashboard';

interface ActionTile {
  key: string;
  label: string;
  sub: string;
  count: number;
  icon: React.ReactNode;
  accent: string;
  to: string;
}

function buildTiles(data: DashboardData): ActionTile[] {
  const q = data.queues;
  const tiles: ActionTile[] = [];
  if (q.quoteApprovals && q.quoteApprovals.total > 0) {
    tiles.push({
      key: 'approvals', label: 'Approve quotes',
      sub: q.quoteApprovals.oldestDays ? `oldest ${q.quoteApprovals.oldestDays} d` : 'waiting on you',
      count: q.quoteApprovals.total, icon: <FactCheckOutlined />, accent: '#FFB74D',
      to: ROUTES.PENDING_APPROVALS,
    });
  }
  if (q.intake && q.intake.total > 0) {
    tiles.push({
      key: 'intake', label: 'Process intake',
      sub: q.intake.oldestDays ? `oldest ${q.intake.oldestDays} d` : 'from the client portal',
      count: q.intake.total, icon: <Inbox />, accent: '#FF8A65',
      to: ROUTES.INTAKE_REQUESTS,
    });
  }
  if (q.review && q.review.total > 0) {
    tiles.push({
      key: 'review', label: 'Review tests',
      sub: q.review.oldestDays ? `oldest ${q.review.oldestDays} d` : 'ready for sign-off',
      count: q.review.total, icon: <RateReviewOutlined />, accent: '#CE93D8',
      to: `${ROUTES.TESTS}?status=Request Review,Review Completed`,
    });
  }
  if (q.dataEntry && q.dataEntry.total > 0) {
    tiles.push({
      key: 'entry', label: 'Enter test data',
      sub: 'scheduled tests', count: q.dataEntry.total,
      icon: <ScienceOutlined />, accent: '#A5D6A7',
      to: `${ROUTES.TESTS}?status=Scheduled`,
    });
  }
  return tiles.slice(0, 3);
}

interface DashboardHeroProps {
  firstName: string;
  data: DashboardData;
}

export const DashboardHero = ({ firstName, data }: DashboardHeroProps) => {
  const navigate = useNavigate();
  const tiles = buildTiles(data);
  const h = new Date().getHours();
  const salute = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  const waiting = data.summary.waitingOnYou;

  return (
    <Box
      sx={{
        borderRadius: '18px',
        background: 'linear-gradient(135deg, #3E4A3E 0%, #4A6741 62%, #5C7A50 100%)',
        color: '#fff',
        p: { xs: 2.5, lg: 3 },
        display: 'flex',
        flexDirection: 'column',
        minHeight: 210,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* soft decorative rings */}
      <Box sx={{ position: 'absolute', right: -70, top: -70, width: 240, height: 240, borderRadius: '50%', border: `1.5px solid ${alpha('#fff', 0.08)}` }} />
      <Box sx={{ position: 'absolute', right: -30, top: -30, width: 160, height: 160, borderRadius: '50%', border: `1.5px solid ${alpha('#fff', 0.1)}` }} />

      <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha('#fff', 0.65) }}>
        {today}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: '1.55rem', lineHeight: 1.15, mb: 0.25 }}>
        {salute}, {firstName}
      </Typography>
      <Typography sx={{ fontSize: '0.85rem', color: alpha('#fff', 0.8), mb: 2 }}>
        {waiting > 0
          ? <>Here&rsquo;s where your attention matters — <b>{waiting}</b> item{waiting === 1 ? '' : 's'} across the lab.</>
          : 'The lab is running clean — nothing needs you right now.'}
      </Typography>

      {tiles.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 'auto', backgroundColor: alpha('#fff', 0.1), borderRadius: '14px', px: 2.5, py: 2 }}>
          <CelebrationOutlined sx={{ fontSize: 34, color: '#A5D6A7' }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>All clear</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: alpha('#fff', 0.75) }}>
              No approvals, intake, reviews or data entry are waiting on you.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${tiles.length}, 1fr)`, gap: 1.5, mt: 'auto' }}>
          {tiles.map((tile) => (
            <Box
              key={tile.key}
              onClick={() => navigate(tile.to)}
              sx={{
                backgroundColor: alpha('#fff', 0.11),
                border: `1px solid ${alpha('#fff', 0.14)}`,
                borderRadius: '14px',
                px: 2, py: 1.5,
                cursor: 'pointer',
                transition: 'all .18s ease',
                '&:hover': { backgroundColor: alpha('#fff', 0.18), transform: 'translateY(-2px)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{
                  width: 38, height: 38, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: alpha(tile.accent, 0.22), color: tile.accent, flexShrink: 0,
                  '& svg': { fontSize: 21 },
                }}>
                  {tile.icon}
                </Box>
                <Typography sx={{ fontSize: '1.7rem', fontWeight: 800, lineHeight: 1 }}>
                  {tile.count}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', mt: 1, lineHeight: 1.2 }}>
                {tile.label}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: alpha('#fff', 0.7) }}>
                {tile.sub}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
